import { useState } from "react";
import { Button, message, Modal, Popconfirm, Tooltip } from "antd";
import { EyeOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import check from "../../assets/images/check.svg";
import classes from "./application-card.module.scss";
import {
  asyncUpdateAppGroup,
  asyncUpdateLeaseStatus,
  asyncCreateNewLeaseVersion,
  asyncGetApplicationGroups,
} from "../../store/easyApp/easyapp-async";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setSelectedAppGroupId } from "../../store/easyApp/easyapp-slice";
import LeaseWizard from "../../screens/products/easyApp/lease-wizard";
import { FaCircle } from "react-icons/fa";
import {
  formatApplicantNames,
  formatDate2,
  getStatusDisplay,
  getSignerTimestamp,
} from "../../screens/products/easyApp/easyapp.utils";
import { getSignedLeaseService } from "../../services/easyapp.service";
import { DenyAdverseActionModal } from "../../screens/products/easyApp/adverse-action";

const ApplicationCard: React.FC<ApplicationCardProps> = ({ application }) => {
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [isDenyWizardOpen, setIsDenyWizardOpen] = useState(false);
  const [isLeaseSent, setIsLeaseSent] = useState(application.lease_sent);
  const [isLeaseUploaded, setIsLeaseUploaded] = useState(
    application.lease_uploaded
  );
  const [viewLoading, setViewLoading] = useState(false);
  const [creatingNewVersion, setCreatingNewVersion] = useState(false);
  const [buttonStates, setButtonStates] = useState({
    approve: false,
    archive: false,
  });

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { roles, user } = useAppSelector((state) => state.user) as {
    roles: UserRole[];
    user: { localAccountId?: string };
  };
  const objectId = user?.localAccountId;

  const formattedApplicants = formatApplicantNames(application.applicant_names);

  const handleUpdateAppGroupField = async (fieldName: string, value: any) => {
    if (fieldName === "status") {
      if (value === "archived" || (value === "submitted" && application.status === "archived"))
        setButtonStates((prev) => ({ ...prev, archive: true }));
      else if (value === "approved")
        setButtonStates((prev) => ({ ...prev, approve: true }));
      else if (value === "denied")
        setButtonStates((prev) => ({ ...prev, deny: true }));
    }    
    
    try {
      await dispatch(
        asyncUpdateAppGroup({
          fieldName,
          value,
          application_group: application.application_group,
        })
      );
    } catch (error) {
      message.error(
        "There was an error updating the application. Please try again."
      );

      if (fieldName === "lease_sent") {
        setIsLeaseSent(application.lease_sent);
      } else if (fieldName === "lease_uploaded") {
        setIsLeaseUploaded(application.lease_uploaded);
      }
    } finally {
      setButtonStates((prev) => ({ ...prev, archive: false, approve: false, deny: false }));
    }
  };

  const handleUpdateLeaseStatus = async (status: any) => {
    try {
      await dispatch(
        asyncUpdateLeaseStatus({
          appGroupID: application.application_group,
          status: status,
        })
      );
    } catch (error) {}
  };

  const handleSeeAllData = () => {
    try {
      dispatch(setSelectedAppGroupId(application.application_group));
      sessionStorage.setItem(
        "selectedAppGroupId",
        application.application_group
      );
      navigate("GroupData");
    } catch (error) {
      Modal.error({
        title: "Navigation Failed",
        content: "Unable to view application data. Please try again.",
      });
    }
  };

  const handleLeaseWizard = async () => {
    setIsWizardOpen(true);
  };

  const handleDenyWizard = async () => {
    setIsDenyWizardOpen(true);
  };

  const handleDenyWizardComplete = async () => {
    try {
      setIsDenyWizardOpen(false)
      await handleUpdateAppGroupField("status", "denied");
    } catch (error) {
      message.error("Failed to deny application. Please try again.");
    }
  };

  const handleWizardComplete = async () => {
    try {
      setIsLeaseSent(true);
      setIsWizardOpen(false);
      await handleUpdateAppGroupField("lease_sent", true);
      message.success("Lease marked as sent.");
    } catch (error) {
      setIsLeaseSent(false);
      message.error("Failed to mark lease as sent. Please try again.");
    }
  };

  const handleView = async () => {
    try {
      setViewLoading(true);
      const response = await getSignedLeaseService(
        application.envelope_details.envelopeId
      );
      const pdfBlob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: "application/pdf" });

      const blobUrl = window.URL.createObjectURL(pdfBlob);
      window.open(blobUrl, "_blank");

      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
    } catch (err) {
      message.error("Failed to fetch signed PDF. Please try again.");
    } finally {
      setViewLoading(false);
    }
  };

  const handleCreateNewLeaseVersion = async () => {
    try {
      setCreatingNewVersion(true);
      
      const result = await dispatch(
        asyncCreateNewLeaseVersion({
          applicationGroupId: application.application_group,
          createdByUserId: objectId,
        })
      );

      if ((result.payload as any)?.success) {
        message.success("New lease version created! You can now edit and send the new lease.");
        // Reset the lease sent state to show the draft UI
        setIsLeaseSent(false);
        // Refresh the application groups to get updated data
        if (objectId) {
          await dispatch(asyncGetApplicationGroups(objectId));
        }
        // Open the lease wizard for the new version
        setIsWizardOpen(true);
      } else {
        message.error("Failed to create new lease version. Please try again.");
      }
    } catch (error) {
      message.error("Failed to create new lease version. Please try again.");
    } finally {
      setCreatingNewVersion(false);
    }
  };

  const hasOnlyRole10 = roles.length === 1 && roles[0]?.role_id === 10;
  const everyStepDisabled = [
    "denied",
    "archived",
    "completed",
  ].includes(application?.status);
  const isStep2Disabled = ["incomplete", "archived"].includes(
    application?.status
  );
  const isStep3Disabled = ![
    "screened",
    "completed",
    "approved",
    "denied",
  ].includes(application?.status);
  const isStep4Disabled = !application.approval_date;

  return (
    <div className={classes.card}>
      <div className={classes.application}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <p className={classes.name}>{formattedApplicants}</p>
            <p className={classes.appDetails2}>
              Application sent {formatDate2(application.submitted_at)}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: "center" }}>
            {application.status === "archived" ? (
              <Popconfirm
                title="Confirm Unarchive"
                description={
                  <div
                    style={{
                      maxWidth: "280px",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    Restore to Current Applications?
                  </div>
                }
                onConfirm={() => {
                  setTimeout(async () => {
                    await handleUpdateAppGroupField("status", "submitted");
                    message.success("Application Unarchived Successfully");
                  }, 0);
                }}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  type="default"
                  className={classes.button}
                  style={{ marginRight: "5px" }}
                  loading={buttonStates.archive}
                >
                  Unarchive
                </Button>
              </Popconfirm>
            ) : (
              <Popconfirm
                title="Confirm Archival"
                description={
                  <div
                    style={{
                      maxWidth: "280px",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    Move to Past Applications?
                  </div>
                }
                onConfirm={() => {
                  setTimeout(async () => {
                    await handleUpdateAppGroupField("status", "archived");
                    message.success("Application Archived Successfully");
                  }, 0);
                }}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  type="default"
                  className={classes.button}
                  disabled={buttonStates.archive || everyStepDisabled}
                  style={{ marginRight: "5px" }}
                  loading={buttonStates.archive}
                >
                  Archive
                </Button>
              </Popconfirm>
            )}
            <Button
              type="default"
              className={classes.button}
              onClick={handleSeeAllData}
            >
              See all data
            </Button>
          </div>
        </div>

        <div className={classes.appDetails}>
          <div>
            <p className={classes.detail}>Property</p>
            <p>{application.property_name || "N/A"}</p>
          </div>
          <div>
            <p className={classes.detail}>Desired Unit</p>
            <p>
              {application.desired_unit_number
                ? `Unit #${application.desired_unit_number}`
                : "N/A"}
            </p>
          </div>
          <div>
            <p className={classes.detail}>Desired Lease length</p>
            <p>
              {application.desired_lease_term
                ? `${application.desired_lease_term} month`
                : "N/A"}
            </p>
          </div>
          <div>
            <p className={classes.detail}>Desired Move in date</p>
            <p>{formatDate2(application.desired_move_in_date)}</p>
          </div>
        </div>
      </div>

      <div className={classes.steps}>
        {/* Step 1: Personal Information */}
        <div className={classes.stepContainer}>
          <p className={classes.stepNum}>Step #1:</p>
          <p className={classes.step}>Personal Information</p>
          <div className={classes.status}>
            {!["incomplete", "archived"].includes(application?.status) && (
              <img src={check} alt="Check mark" />
            )}

            <p>
              {application?.status === "archived"
                ? ""
                : application?.status === "incomplete"
                ? !application?.ms_property_id
                  ? "Not-Started"
                  : "In Progress"
                : "Completed"}
            </p>
          </div>

          {/* Show Payment Timestamps per Applicant */}
          {application?.applicant_names?.some((a: any) => a.payment_date) && (
            <div className={classes.plaidInfo} style={{ marginTop: "10px" }}>
              <span className={classes.plaidInfoTitle}>Application Fee Paid</span>
              <div className={classes.incomeList}>
                {application.applicant_names
                  .filter((applicant: any) => applicant.payment_date)
                  .map((applicant: any, idx: number) => (
                    <div key={idx} className={classes.incomeItem}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span className={classes.applicantName}>
                          {applicant.first_name} {applicant.last_name}
                        </span>
                        <span style={{ fontSize: "12px", color: "#666" }}>
                          {formatDate2(applicant.payment_date)}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>

        {/* Step 2: Fees & Verification */}
        <div style={{ position: "relative", display: "flex" }}>
          {isStep2Disabled && <div className={classes.disabled}></div>}
          <div className={classes.stepContainer}>
            <p className={classes.stepNum}>Step #2:</p>
            <p className={classes.step}>Fees/Verification & Screening</p>
            {!isStep2Disabled && (
              <div className={classes.status}>
                {(application?.status === "screened" ||
                  application?.status === "completed" ||
                  application?.status === "approved" ||
                  application?.status === "denied") && (
                  <img src={check} alt="Check mark" />
                )}
                <p>
                  {application?.status === "completed"
                    ? "Screening Completed"
                    : application?.status === "approved"
                    ? "Screening Completed"
                    : application?.status === "denied"
                    ? "Screening Completed"
                    : application?.status === "screened"
                    ? "Screening Completed"
                    : application?.status === "submitted"
                    ? "Ready to Screen"
                    : application?.status === "income-verification"
                    ? "Income Pending"
                    : application?.status === "id-verification-failed"
                    ? "ID Rejected"
                    : application?.status === "id-verification"
                    ? "ID Pending Upload"
                    : application?.status === "payment"
                    ? "Awaiting Payment"
                    : "Awaiting Payment"}
                </p>
              </div>
            )}

            {/* Show ID Rejected applicants */}
            {application?.status === "id-verification-failed" && 
              application?.applicant_names?.some((a: any) => a.app_status === "id-verification-failed") && (
              <div className={classes.plaidInfo}>
                <span className={classes.plaidInfoTitle}>ID Rejected For:</span>
                <div className={classes.idRejectedList}>
                  {application.applicant_names
                    .filter((a: any) => a.app_status === "id-verification-failed")
                    .map((applicant: any, idx: number) => (
                      <div key={idx} className={classes.idRejectedItem}>
                        <span className={classes.applicantName}>
                          {applicant.first_name} {applicant.last_name}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Show Verified Income per applicant (Plaid + Manual) */}
            {application?.applicant_names?.some((a: any) => 
              a.verified_income > 0 || a.manual_income > 0 || a.is_guarantor || a.skipped_income_verification || (a.income_deduplicated_from !== null && a.income_deduplicated_from !== undefined)
            ) && (
              <div className={classes.plaidInfo}>
                <span className={classes.plaidInfoTitle}>Verified Income</span>
                <div className={classes.incomeList}>
                  {application.applicant_names.map((applicant: any, idx: number) => {
                    const isDeduplicatedIncome = applicant.income_deduplicated_from !== null && applicant.income_deduplicated_from !== undefined;
                    const deduplicatedFromName = isDeduplicatedIncome
                      ? application.applicant_names[applicant.income_deduplicated_from]?.first_name || 'Applicant 1'
                      : null;
                    
                    const plaidIncome = applicant.verified_income || 0;
                    const manualIncome = applicant.manual_income || 0;
                    const totalIncome = plaidIncome + manualIncome;
                    const hasPlaidIncome = plaidIncome > 0;
                    const hasManualIncome = manualIncome > 0;
                    const hasBothIncomes = hasPlaidIncome && hasManualIncome;
                    
                    return (
                      <div key={idx} className={classes.incomeItem}>
                        <div className={classes.incomeRow}>
                          <span className={classes.applicantName}>
                            {applicant.first_name}
                          </span>
                          {totalIncome > 0 ? (
                            <span className={hasBothIncomes ? classes.combinedIncome : hasManualIncome ? classes.manualIncome : classes.incomeAmount}>
                              ${totalIncome.toLocaleString()}/mo
                            </span>
                          ) : isDeduplicatedIncome ? (
                            <span className={classes.deduplicatedIncome}>
                              Joint account
                            </span>
                          ) : (
                            <span className={classes.noIncome}>
                              {applicant.skipped_income_verification ? 'Opted out' : '—'}
                            </span>
                          )}
                        </div>
                        <div className={classes.badgeRow}>
                          {/* Income source badges */}
                          {hasBothIncomes && (
                            <span className={classes.combinedBadge}>✓ Plaid + Manual</span>
                          )}
                          {hasPlaidIncome && !hasManualIncome && (
                            <span className={classes.plaidBadge}>✓ Plaid</span>
                          )}
                          {hasManualIncome && !hasPlaidIncome && (
                            <span className={classes.manualBadge}>✏️ Manual</span>
                          )}
                          {/* Deduplication badge */}
                          {isDeduplicatedIncome && deduplicatedFromName && (
                            <span className={classes.deduplicatedBadge}>🔗 Counted under {deduplicatedFromName}</span>
                          )}
                          {/* Guarantor badges - if opted out of income, they CAN'T be a guarantor */}
                          {applicant.skipped_income_verification ? (
                            <span className={classes.willUseGuarantorBadge}>👤 Will use guarantor/roommate</span>
                          ) : applicant.is_guarantor ? (
                            <span className={classes.isGuarantorBadge}>🛡️ Is a guarantor</span>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Step 3: Approve or deny applicant */}
        <div style={{ position: "relative", display: "flex" }}>
          {isStep3Disabled && <div className={classes.disabled}></div>}
          <div className={classes.stepContainer}>
            <p className={classes.stepNum}>Step #3:</p>
            <p className={classes.step}>Approve or deny applicant</p>

            {/* Show TransUnion Recommendation if available */}
            {application.screening_recommendation && (
              <div className={classes.plaidInfo} style={{ marginBottom: '10px' }}>
                <span className={classes.plaidInfoTitle}>TransUnion Recommendation</span>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  marginTop: '4px'
                }}>
                  <FaCircle 
                    style={{ 
                      color: application.screening_recommendation.toLowerCase().includes('decline') 
                        ? '#dc2626' 
                        : application.screening_recommendation.toLowerCase().includes('accept')
                          ? '#22c55e'
                          : '#f59e0b',
                      height: '10px',
                      width: '10px',
                      flexShrink: 0
                    }} 
                  />
                  <span style={{ fontWeight: 600, fontSize: '13px' }}>
                    {application.screening_recommendation}
                  </span>
                </div>
              </div>
            )}

            {!application.approval_date && application?.status !== "denied" && (
              <div className={classes.btns}>
                <Button
                  type="default"
                  style={{ fontFamily: "Lato" }}
                  onClick={handleDenyWizard}
                  disabled={
                    hasOnlyRole10 || isStep3Disabled || everyStepDisabled
                  }
                >
                  Deny
                </Button>
                <Popconfirm
                  title="Confirm Approval"
                  description={
                    <div
                      style={{
                        maxWidth: "280px",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }}
                    >
                      Approve this Application?
                    </div>
                  }
                  onConfirm={() => {
                    setTimeout(async () => {
                      await Promise.all([
                        handleUpdateAppGroupField("status", "approved"),
                        handleUpdateAppGroupField(
                          "approval_date",
                          new Date().toISOString()
                        ),
                      ]);
                      message.success("Application Approved Successfully");
                    }, 0);
                  }}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    type="primary"
                    style={{ fontFamily: "Lato" }}
                    disabled={
                      buttonStates.approve ||
                      hasOnlyRole10 ||
                      isStep3Disabled ||
                      everyStepDisabled
                    }
                    loading={buttonStates.approve}
                  >
                    Approve
                  </Button>
                </Popconfirm>
              </div>
            )}

            {application?.status === "denied" && (
              <p style={{ fontWeight: "900", marginTop: "10px" }}>
                Application denied.
              </p>
            )}

            {application.approval_date && (
              <div className={classes.status}>
                <img src={check} alt="Check mark" />
                <p>Approved {formatDate2(application.approval_date)}</p>
              </div>
            )}
          </div>
        </div>
        <DenyAdverseActionModal
          open={isDenyWizardOpen}
          onComplete={handleDenyWizardComplete}
          onCancel={() => setIsDenyWizardOpen(false)}
          application={application}
        />

        {/* Step 4: Generate and send lease */}
        <div style={{ position: "relative", display: "flex" }}>
          {isStep4Disabled && <div className={classes.disabled}></div>}
          <div className={classes.stepContainer}>
            <p className={classes.stepNum}>Step #4:</p>
            {isLeaseSent ? (
              <>
                {application.envelope_details?.status === "completed" ? (
                  <>
                    <p
                      className={classes.step}
                      style={{
                        justifyContent: "space-between",
                        display: "flex",
                      }}
                    >
                      Lease Signatures
                      <Tooltip title="View Signed Lease">
                        <Button
                          type="default"
                          shape="circle"
                          icon={<EyeOutlined />}
                          loading={viewLoading}
                          onClick={handleView}
                          style={{ top: "-18px" }}
                        />
                      </Tooltip>
                    </p>
                    <div className={classes.status}>
                      <img src={check} alt="Check mark" />
                      <p>Signed & Completed</p>
                    </div>
                  </>
                ) : (
                  <>
                    {application?.envelope_details ? (
                      <p>Sent - awaiting signatures</p>
                    ) : (
                      <p className={classes.step}>Sent - awaiting signatures</p>
                    )}
                    <div style={{ marginTop: "1px" }}>
                      {application?.envelope_details ? (
                        (() => {
                          const tenants =
                            application.envelope_details?.recipients?.signers.filter(
                              (s: any) =>
                                s.roleName?.toLowerCase().startsWith("tenant")
                            );
                          const owners =
                            application.envelope_details?.recipients?.signers.filter(
                              (s: any) =>
                                s.roleName?.toLowerCase().startsWith("owner")
                            );

                          const renderSigners = (signers: any[]) =>
                            (signers || []).map((signer) => {
                              const { text, color } = getStatusDisplay(
                                signer.status
                              );
                              const timestamp = getSignerTimestamp(signer);
                              return (
                                <div
                                  key={signer.recipientId}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                  }}
                                >
                                  <FaCircle style={{ color, height: "12px" }} />
                                  <p style={{ fontSize: "12px" }}>
                                    {signer.name} - {text}
                                    {timestamp && (
                                      <span style={{ color: "#666", marginLeft: "4px" }}>
                                        ({timestamp})
                                      </span>
                                    )}
                                  </p>
                                </div>
                              );
                            });

                          return (
                            <>
                              {tenants.length > 0 && (
                                <div style={{ marginBottom: "6px" }}>
                                  <p style={{ fontSize: "12px" }}>Tenants:</p>
                                  <div>{renderSigners(tenants)}</div>
                                </div>
                              )}
                              {owners.length > 0 && (
                                <div>
                                  <p style={{ fontSize: "12px" }}>Owner:</p>
                                  <div>{renderSigners(owners)}</div>
                                </div>
                              )}
                            </>
                          );
                        })()
                      ) : (
                        <div className={classes.status}>
                          <p>In Progress</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Create New Lease Version Button */}
                    <div style={{ marginTop: "12px" }}>
                      <Popconfirm
                        title="Create New Lease Version"
                        description={
                          <div
                            style={{
                              maxWidth: "280px",
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                            }}
                          >
                            This will supersede the current lease and create a new draft version. 
                            The previous lease details will be copied as a starting point.
                          </div>
                        }
                        onConfirm={handleCreateNewLeaseVersion}
                        okText="Create New Version"
                        cancelText="Cancel"
                      >
                        <Button
                          type="default"
                          icon={<PlusOutlined />}
                          loading={creatingNewVersion}
                          disabled={creatingNewVersion || everyStepDisabled}
                          style={{ fontFamily: "Lato", fontSize: "12px" }}
                        >
                          Create New Lease
                        </Button>
                      </Popconfirm>
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                <label className={classes.step}>Draft & Send Lease</label>
                <div className={classes.btns}>
                  <Button
                    type="primary"
                    style={{ fontFamily: "Lato" }}
                    onClick={handleLeaseWizard}
                    disabled={
                      isStep4Disabled ||
                      isLeaseSent ||
                      everyStepDisabled
                    }
                  >
                    Create/Resume Lease
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
        <LeaseWizard
          open={isWizardOpen}
          onCancel={() => setIsWizardOpen(false)}
          onComplete={handleWizardComplete}
          appGroupID={application.application_group}
        />
      </div>
    </div>
  );
};

export default ApplicationCard;
